import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../../../ui/dialog";
import { Badge } from "../../../../../ui/badge";
import { ScrollArea } from "../../../../../ui/scroll-area";
import { Skeleton } from "../../../../../ui/skeleton";
import { supabase } from "../../../../../../integrations/supabase/client";
import { format } from "date-fns";
import { Star } from "lucide-react";

interface Review {
  id: string;
  author_name: string;
  comment_text: string;
  rating: number;
  posted_at: string;
  platform: string;
  sentiment?: string;
}

interface KeywordReviewsModalProps {
  open: boolean;
  onClose: () => void;
  keyword: string;
  type: "keyword" | "topic";
  dateRange: string;
  platform: string;
  outletIds?: string[];
  supabaseoutletIDs?: string[],
}

export const KeywordReviewsModal = ({
  open,
  onClose,
  keyword,
  type,
  dateRange,
  platform,
  outletIds,
  supabaseoutletIDs
}: KeywordReviewsModalProps) => {
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState<Review[]>([]);

  useEffect(() => {
    if (open && keyword) {
      loadReviews();
    }
  }, [open, keyword, dateRange, platform, outletIds]);

  const loadReviews = async () => {
    setLoading(true);
    try {
      const daysAgo = dateRange === "7days" ? 7 : dateRange === "30days" ? 30 : 90;
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - daysAgo);

      let query = supabase
        .from("reviews")
        .select("id, author_name, comment_text, rating, posted_at, platform, sentiment, topics")
        .gte("posted_at", startDate.toISOString());

      if (platform !== "all") {
        query = query.eq("platform", platform as any);
      }

      const isAllSelected = !outletIds || outletIds.length === 0 || outletIds.includes("all");
      if (!isAllSelected && outletIds.length > 0) {
        query = query.in("outlet_id", outletIds);
      }else {
        query = query.in('outlet_id', supabaseoutletIDs);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Filter reviews based on type
      const filteredReviews = data?.filter((review) => {
        if (type === "keyword") {
          // Check if comment contains the keyword
          return review.comment_text
            ?.toLowerCase()
            .includes(keyword.toLowerCase());
        } else {
          // Check if topics array contains the topic
          return review.topics?.some(
            (topic: string) => topic.toLowerCase() === keyword.toLowerCase()
          );
        }
      }) || [];

      setReviews(filteredReviews);
    } catch (error) {
      console.error("Error loading reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  const getSentimentColor = (sentiment?: string) => {
    switch (sentiment) {
      case "positive":
        return "bg-green-500";
      case "negative":
        return "bg-red-500";
      case "neutral":
        return "bg-gray-500";
      default:
        return "bg-gray-400";
    }
  };

  const getPlatformEmoji = (platform: string) => {
    switch (platform.toLowerCase()) {
      case "google_maps":
        return "🗺️";
      case "instagram":
        return "📷";
      case "tiktok":
        return "🎵";
      case "facebook":
        return "👍";
      case "tripadvisor":
        return "✈️";
      default:
        return "📝";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>
            Reviews with {type === "keyword" ? "keyword" : "topic"}: "{keyword}"
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[60vh] pr-4">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-32 w-full" />
              ))}
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No reviews found with this {type}.
            </div>
          ) : (
            <div className="space-y-4">
              {reviews.map((review) => (
                <div
                  key={review.id}
                  className="border rounded-lg p-4 space-y-2 hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm">
                          {review.author_name || "Anonymous"}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {getPlatformEmoji(review.platform)} {review.platform.replace("_", " ")}
                        </Badge>
                        {review.sentiment && (
                          <Badge
                            variant="outline"
                            className={`text-xs text-white ${getSentimentColor(
                              review.sentiment
                            )}`}
                          >
                            {review.sentiment}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1 mb-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`h-4 w-4 ${
                              star <= review.rating
                                ? "fill-yellow-400 text-yellow-400"
                                : "text-gray-300"
                            }`}
                          />
                        ))}
                        <span className="text-xs text-muted-foreground ml-2">
                          {format(new Date(review.posted_at), "MMM d, yyyy")}
                        </span>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm leading-relaxed">{review.comment_text}</p>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
